import { BaseAPI } from "./BaseAPI";

import type { APIConf } from "./BaseAPI";
import type {
  CountryGroup,
  CountryGroupPayload,
  CreateCountryGroupResponse,
  CreateEmbedProfileResponse,
  CreateStorageAccessResponse,
  DisconnectPaypalAccountParams,
  DisconnectPaypalAccountResponse,
  EmbedProfile,
  EmbedProfilePayload,
  EmbedProfileUsage,
  GetComponentListParams,
  GetComponentListResponse,
  GetComponentResponse,
  GetCountriesResponse,
  GetCountryGroupListResponse,
  GetEmbedProfileListResponse,
  GetEmbedProfileOptionListResponse,
  GetLanguagesResponse,
  GetPaypalConnectLinkParams,
  GetPaypalConnectLinkResponse,
  GetPlatformsParams,
  GetPlatformsResponse,
  GetProjectListParams,
  GetProjectPageComponentListResponse,
  GetProjectPageListResponse,
  GetProjectPageResponse,
  GetProjectResponse,
  GetStorageAccessListResponse,
  GetStorageAccessOptionListResponse,
  NewProjectPage,
  PlatformComponent,
  ProjectPage,
  ProjectsResponse,
  StorageAccessPayload,
  TestStorageAccessConnectionParams,
  UpdateComponentParams,
  UpdateEmbedProfileParams,
  UpdateProjectParams,
  UpdateProjectResponse,
  UpdateStorageAccessParams,
} from "./ProjectAPI.types";
import type { OrganizationParamsResponse } from "../types/organization.types";

class ProjectAPIClass extends BaseAPI {
  constructor(conf: APIConf) {
    super(conf);
    this.service = "project";
    this.version = "v4";
  }

  async getPlatforms({
    projectSlug,
  }: GetPlatformsParams): Promise<GetPlatformsResponse> {
    return this.axios
      .get<GetPlatformsResponse>(
        `${this.routeBase}/admin/storefront_projects/offer_plans_options`,
        {
          headers: { "xx-ok-project": projectSlug },
        },
      )
      .then((response) => response.data);
  }

  async updateBackgroundImage({
    component,
    image,
    projectSlug,
  }: {
    component: string;
    image: string;
    projectSlug: string;
  }): Promise<string> {
    return this.axios
      .post<string>(
        `${this.routeBase}/admin/storefront_projects/background`,
        {
          image,
          component,
        },
        {
          headers: { "xx-ok-project": projectSlug },
        },
      )
      .then((response) => response.data);
  }

  async getBackgroundImage({
    component,
    projectSlug,
  }: {
    component: string;
    projectSlug: string;
  }): Promise<{ background_picture_url: string }> {
    return this.axios
      .get<{ background_picture_url: string }>(
        `${this.routeBase}/admin/storefront_projects/background`,
        {
          headers: { "xx-ok-project": projectSlug },
          params: { component },
        },
      )
      .then((response) => response.data);
  }

  async deleteBackgroundImage({
    component,
    projectSlug,
  }: {
    component: string;
    projectSlug: string;
  }): Promise<unknown> {
    return this.axios
      .delete(`${this.routeBase}/admin/storefront_projects/background`, {
        headers: { "xx-ok-project": projectSlug },
        params: {
          component,
        },
      })
      .then((response) => response.data);
  }

  async getProjectList(
    params: GetProjectListParams = {},
  ): Promise<ProjectsResponse> {
    return this.axios
      .get<ProjectsResponse>(`${this.routeBase}/admin/storefront_projects`, {
        params: { uuids: params.uuids },
      })
      .then((response) => response.data);
  }

  async getProject(projectSlug: string): Promise<GetProjectResponse> {
    return this.axios
      .get<GetProjectResponse>(
        `${this.routeBase}/admin/storefront_projects/${projectSlug}`,
        {
          headers: { "xx-ok-project": projectSlug },
        },
      )
      .then((response) => response.data);
  }

  async getLanguages(): Promise<GetLanguagesResponse> {
    return this.axios.get(`${this.routeBase}/admin/languages`);
  }

  async createComponent<T extends GetComponentResponse>({
    component,
    projectSlug,
  }: {
    component: Omit<T, "uuid">;
    projectSlug: string;
  }): Promise<T> {
    return this.axios
      .post<T>(
        `${this.apiBase}/${this.service}/v3/admin/components/create`,
        component,
        {
          headers: { "xx-ok-project": projectSlug },
        },
      )
      .then((response) => response.data);
  }

  async getComponent<T extends GetComponentResponse>({
    projectSlug,
    componentId,
  }: {
    projectSlug: string;
    componentId: string;
  }): Promise<T> {
    return this.axios
      .get<T>(
        `${this.apiBase}/${this.service}/v3/admin/components/${componentId}`,
        {
          headers: { "xx-ok-project": projectSlug },
        },
      )
      .then((response) => response.data);
  }

  async updateComponent<T extends PlatformComponent>({
    component,
    projectSlug,
  }: UpdateComponentParams<T>): Promise<T> {
    return this.axios
      .put<T>(
        `${this.apiBase}/${this.service}/v3/admin/components/${component.uuid}`,
        component,
        {
          headers: { "xx-ok-project": projectSlug },
        },
      )
      .then((response) => response.data);
  }

  async deleteComponent({
    componentId,
    projectSlug,
  }: {
    componentId: string;
    projectSlug: string;
  }): Promise<void> {
    return this.axios.delete(
      `${this.apiBase}/${this.service}/v3/admin/components/${componentId}`,
      {
        headers: { "xx-ok-project": projectSlug },
      },
    );
  }

  async getComponentList<
    T extends GetComponentResponse = GetComponentResponse,
  >({
    projectSlug,
    sectionList,
  }: GetComponentListParams): Promise<GetComponentListResponse<T>> {
    const params: {
      page: number;
      per_page: number;
      section?: string;
    } = {
      page: 1,
      per_page: 10000,
    };

    if (sectionList) {
      params.section = sectionList.join(",");
    }

    return this.axios
      .get<GetComponentListResponse<T>>(
        `${this.apiBase}/${this.service}/v3/admin/components`,
        {
          headers: { "xx-ok-project": projectSlug },
          params,
        },
      )
      .then((response) => response.data);
  }

  async getPaypalConnectLink({
    projectSlug,
  }: GetPaypalConnectLinkParams): Promise<GetPaypalConnectLinkResponse> {
    return this.axios
      .get<GetPaypalConnectLinkResponse>(
        `${this.routeBase}/admin/storefront_projects/paypal_connect`,
        {
          headers: { "xx-ok-project": projectSlug },
        },
      )
      .then((response) => response.data);
  }

  async disconnectPaypalAccount({
    projectSlug,
  }: DisconnectPaypalAccountParams): Promise<DisconnectPaypalAccountResponse> {
    return this.axios
      .delete<DisconnectPaypalAccountResponse>(
        `${this.routeBase}/admin/storefront_projects/paypal_connect`,
        {
          headers: { "xx-ok-project": projectSlug },
        },
      )
      .then((response) => response.data);
  }

  async getProjectPageList(
    projectSlug: string,
  ): Promise<GetProjectPageListResponse> {
    return this.axios
      .get<GetProjectPageListResponse>(
        `${this.routeBase}/admin/storefront_projects/pages`,
        {
          headers: { "xx-ok-project": projectSlug },
        },
      )
      .then((response) => response.data);
  }

  async getProjectPage({
    pageId,
    projectSlug,
  }: {
    pageId: string;
    projectSlug: string;
  }): Promise<GetProjectPageResponse> {
    return this.axios
      .get<GetProjectPageResponse>(
        `${this.routeBase}/admin/storefront_projects/pages/${pageId}`,
        {
          headers: { "xx-ok-project": projectSlug },
        },
      )
      .then((response) => response.data);
  }

  async updateProjectPage({
    pageId,
    projectSlug,
    updatedPage,
  }: {
    pageId: string;
    projectSlug: string;
    updatedPage: ProjectPage;
  }): Promise<GetProjectPageResponse> {
    return this.axios
      .put<GetProjectPageResponse>(
        `${this.routeBase}/admin/storefront_projects/pages/${pageId}`,
        updatedPage,
        {
          headers: { "xx-ok-project": projectSlug },
        },
      )
      .then((response) => response.data);
  }

  async createProjectPage({
    projectSlug,
    newPage,
  }: {
    projectSlug: string;
    newPage: NewProjectPage;
  }): Promise<GetProjectPageResponse> {
    return this.axios
      .post<GetProjectPageResponse>(
        `${this.routeBase}/admin/storefront_projects/pages`,
        newPage,
        {
          headers: { "xx-ok-project": projectSlug },
        },
      )
      .then((response) => response.data);
  }

  async projectPageRankUp({
    projectSlug,
    pageId,
  }: {
    projectSlug: string;
    pageId: string;
  }): Promise<GetProjectPageResponse> {
    return this.axios
      .put<GetProjectPageResponse>(
        `${this.routeBase}/admin/storefront_projects/pages/${pageId}/rank_up`,
        {},
        {
          headers: { "xx-ok-project": projectSlug },
        },
      )
      .then((response) => response.data);
  }

  async projectPageRankDown({
    projectSlug,
    pageId,
  }: {
    projectSlug: string;
    pageId: string;
  }): Promise<GetProjectPageResponse> {
    return this.axios
      .put<GetProjectPageResponse>(
        `${this.routeBase}/admin/storefront_projects/pages/${pageId}/rank_down`,
        {},
        {
          headers: { "xx-ok-project": projectSlug },
        },
      )
      .then((response) => response.data);
  }

  async deleteProjectPage({
    pageId,
    projectSlug,
  }: {
    pageId: string;
    projectSlug: string;
  }): Promise<void> {
    return this.axios.delete(
      `${this.routeBase}/admin/storefront_projects/pages/${pageId}`,
      {
        headers: { "xx-ok-project": projectSlug },
      },
    );
  }

  async getProjectPageComponentList({
    pageId,
    platform,
    projectSlug,
  }: {
    pageId: string;
    platform: string;
    projectSlug: string;
  }): Promise<GetProjectPageComponentListResponse> {
    return this.axios
      .get<GetProjectPageComponentListResponse>(
        `${this.routeBase}/admin/storefront_projects/pages/${pageId}/components`,
        {
          headers: { "xx-ok-project": projectSlug },
          params: { platform },
        },
      )
      .then((response) => response.data);
  }

  setDefaultHomepage = async ({
    pageId,
    projectSlug,
  }: {
    pageId: string;
    projectSlug: string;
  }): Promise<void> =>
    this.axios.post(
      `${this.routeBase}/admin/storefront_projects/pages/${pageId}/set_default`,
      {},
      {
        headers: { "xx-ok-project": projectSlug },
      },
    );

  async updateProject({
    projectSlug,
    data,
  }: UpdateProjectParams): Promise<UpdateProjectResponse> {
    return this.axios
      .put<UpdateProjectResponse>(
        `${this.apiBase}/${this.service}/v3/admin/storefront_projects`,
        data,
        {
          headers: { "xx-ok-project": projectSlug },
        },
      )
      .then((response) => response.data);
  }

  async getOrganizationParams(): Promise<OrganizationParamsResponse> {
    return this.axios
      .get<OrganizationParamsResponse>(
        `${this.apiBase}/${this.service}/v4/admin/organization_params`,
      )
      .then((response) => response.data);
  }

  async getCountryList(): Promise<GetCountriesResponse> {
    return this.axios
      .get<GetCountriesResponse>(`${this.routeBase}/countries`)
      .then((response) => response.data);
  }

  async getCountryGroupList(): Promise<GetCountryGroupListResponse> {
    return this.axios
      .get<GetCountryGroupListResponse>(
        `${this.routeBase}/admin/country_groups`,
      )
      .then((response) => response.data);
  }

  async createCountryGroup({
    data,
  }: {
    data: CountryGroupPayload;
  }): Promise<CreateCountryGroupResponse> {
    return this.axios
      .post<CreateCountryGroupResponse>(
        `${this.routeBase}/admin/country_groups`,
        data,
      )
      .then((response) => response.data);
  }

  async updateCountryGroup({
    uuid,
    data,
  }: {
    uuid: string;
    data: CountryGroupPayload;
  }): Promise<CountryGroup> {
    return this.axios
      .put<CountryGroup>(`${this.routeBase}/admin/country_groups/${uuid}`, data)
      .then((response) => response.data);
  }

  async deleteCountryGroup({ uuid }: { uuid: string }): Promise<void> {
    await this.axios.delete(`${this.routeBase}/admin/country_groups/${uuid}`);
  }

  async getStorageAccessList(): Promise<GetStorageAccessListResponse> {
    return this.axios
      .get<GetStorageAccessListResponse>(
        `${this.routeBase}/admin/storage_credentials`,
      )
      .then((response) => response.data);
  }

  async getStorageAccessOptions(): Promise<GetStorageAccessOptionListResponse> {
    return this.axios
      .get<GetStorageAccessOptionListResponse>(
        `${this.routeBase}/admin/storage_credentials_options`,
      )
      .then((response) => response.data);
  }

  async createStorageAccess(
    data: StorageAccessPayload,
  ): Promise<CreateStorageAccessResponse> {
    return this.axios
      .post<CreateStorageAccessResponse>(
        `${this.routeBase}/admin/storage_credentials`,
        data,
      )
      .then((response) => response.data);
  }

  async updateStorageAccess({
    uuid,
    data,
  }: UpdateStorageAccessParams): Promise<void> {
    await this.axios.put(
      `${this.routeBase}/admin/storage_credentials/${uuid}`,
      data,
    );
  }

  async deleteStorageAccess(uuid: string): Promise<void> {
    await this.axios.delete(
      `${this.routeBase}/admin/storage_credentials/${uuid}`,
    );
  }

  async testStorageAccessConnection({
    uuid,
    data,
  }: TestStorageAccessConnectionParams): Promise<void> {
    await this.axios.post(
      `${this.routeBase}/admin/storage_credentials/${uuid}/test`,
      data,
    );
  }

  async testStorageAccessCredentials(
    data: StorageAccessPayload,
  ): Promise<void> {
    await this.axios.post(
      `${this.routeBase}/admin/storage_credentials/test`,
      data,
    );
  }

  async getEmbedProfileList(): Promise<GetEmbedProfileListResponse> {
    return this.axios
      .get<GetEmbedProfileListResponse>(
        `${this.routeBase}/admin/embed_profiles`,
      )
      .then((response) => response.data);
  }

  async getEmbedProfileOptions(): Promise<GetEmbedProfileOptionListResponse> {
    return this.axios
      .get<GetEmbedProfileOptionListResponse>(
        `${this.routeBase}/admin/embed_profiles_options`,
      )
      .then((response) => response.data);
  }

  async createEmbedProfile(
    data: EmbedProfilePayload,
  ): Promise<CreateEmbedProfileResponse> {
    return this.axios
      .post<CreateEmbedProfileResponse>(
        `${this.routeBase}/admin/embed_profiles`,
        data,
      )
      .then((response) => response.data);
  }

  async updateEmbedProfile({
    uuid,
    data,
  }: UpdateEmbedProfileParams): Promise<EmbedProfile> {
    return this.axios
      .put<EmbedProfile>(`${this.routeBase}/admin/embed_profiles/${uuid}`, data)
      .then((response) => response.data);
  }

  async deleteEmbedProfile(uuid: string): Promise<void> {
    await this.axios.delete(`${this.routeBase}/admin/embed_profiles/${uuid}`);
  }

  async getEmbedProfileUsage(uuid: string): Promise<EmbedProfileUsage> {
    return this.axios
      .get<EmbedProfileUsage>(
        `${this.routeBase}/admin/embed_profiles/${uuid}/usage`,
      )
      .then((response) => response.data);
  }
}

const ProjectAPI = new ProjectAPIClass({
  token: localStorage.token,
});

export default ProjectAPI;
